import { useMutation } from '@tanstack/react-query';
import { AGGREGATE_NAMES, EUROPACE_PORTAL_LINK_KINDS, LOAN_REQUEST_RESOLVERS } from 'cpool-constants';
import { schemaLoanRequestResolver_GetEuropacePortalLink } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { buildQueryString } from '~/queryUtils/useQuery.ts';

type Variables = {
    // opened synchronously on click, otherwise the popup blocker stops the late window.open
    portalWindow: Window | null;
};

class PortalLinkExpiredError extends Error {}

// token stays in this closure only: no query cache, no storage, mutation returns nothing
export const useOpenEuropacePortalLink = ({
    europaceOfferId,
    loanRequestId,
}: {
    europaceOfferId: string | null | undefined;
    loanRequestId: string | undefined;
}) => {
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async ({ portalWindow }: Variables) => {
            const portalLinkResponse = await APICommunicator.apiFetch({
                context: { cache: 'no-store' },
                route: `/query/${AGGREGATE_NAMES.LOAN_REQUEST}/${
                    LOAN_REQUEST_RESOLVERS.getEuropacePortalLink
                }${buildQueryString({ europaceOfferId, id: loanRequestId })}`,
                shouldThrow: false,
            });
            if (!portalLinkResponse.ok) throw new Error(`Portal link request failed (${portalLinkResponse.status})`);
            const portalLink = schemaLoanRequestResolver_GetEuropacePortalLink.parse(await portalLinkResponse.json());
            if (portalLink.kind === EUROPACE_PORTAL_LINK_KINDS.EXPIRED) throw new PortalLinkExpiredError();
            if (portalLink.kind !== EUROPACE_PORTAL_LINK_KINDS.AVAILABLE) throw new Error('Portal link unavailable');

            const bankResponse = await fetch(portalLink.url, {
                credentials: 'include',
                headers: { Authorization: portalLink.authorization },
                method: 'POST',
            });
            if (!bankResponse.ok) throw new Error(`Bank portal request failed (${bankResponse.status})`);

            if (portalWindow) portalWindow.location.assign(bankResponse.url);
            else window.open(bankResponse.url, '_blank');
        },
        onError: (error, { portalWindow }) => {
            portalWindow?.close();
            if (error instanceof PortalLinkExpiredError) {
                toast.info(t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.TOAST_INFO.PORTAL_LINK_EXPIRED'));
                return;
            }
            toast.info(t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.TOAST_INFO.OPEN_PORTAL_IN_EUROPACE'));
        },
    });
};
